import { getPosthogConfig, isPosthogEnabled } from './config';

/**
 * Evaluates one flag for someone other than the signed-in admin, e.g. an office
 * the admin is posting for, through PostHog's public `/flags` endpoint.
 *
 * Deliberately not posthog-js: the SDK answers for the identified admin and
 * fires `$feature_flag_called`, which would enrol them and record an exposure.
 * This request captures nothing, and without `$anon_distinct_id` PostHog never
 * writes an experience-continuity override, so it can't move the person's arm.
 * Plain `fetch` rather than `apis/tempMee`, whose interceptor would attach the
 * admin's access token.
 *
 * `environment` is sent as a property override so a person PostHog has not
 * seen yet still matches the per-environment release conditions; `appType`
 * is the value PosthogService registers for this app. Overrides only apply to
 * this evaluation and are never saved on the person.
 *
 * @param {string} flagKey
 * @param {string} distinctId - The other person's PostHog distinct id (their user id)
 * @returns {Promise<{ variant: string|boolean|null, payload: unknown }|null>} null when PostHog is off or the call fails
 */
export const evaluateFlagForDistinctId = async (flagKey, distinctId) => {
  if (!distinctId || !isPosthogEnabled()) return null;

  const { apiKey, apiHost } = getPosthogConfig();

  try {
    const response = await fetch(`${apiHost}/flags/?v=2`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        api_key: apiKey,
        distinct_id: distinctId,
        person_properties: {
          environment: process.env.REACT_APP_TEMP_MEE_ENV || 'development',
          appType: 'admin_portal',
        },
        flag_keys_to_evaluate: [flagKey],
      }),
    });

    if (!response.ok) return null;

    const flag = (await response.json())?.flags?.[flagKey];
    if (!flag) return null;

    return {
      variant: flag.variant ?? flag.enabled ?? null,
      payload: flag.metadata?.payload ?? null,
    };
  } catch {
    return null;
  }
};
